import React, { type FC, useContext, type ReactNode, type PropsWithChildren } from 'react';

import styled from '@emotion/styled';
import { getI18nData, sendBeaconEvent, formatLanguageKey, isBrowser, prependInternalLink } from '@lucid/utils';

import type { Lucid } from '../../types/types';
import Image from '../../components/image/image';
import Box from '../../components/box/box';
import Container from '../../components/container/container';
import LayoutContext from '../../components/context/layout-context';
import LanguageSwitcher from '../../components/language-switcher/language-switcher';
import Link from '../../components/link/link';
import Text from '../../components/text/text';
import SocialIconSection from '../../components/social-icon-section/social-icon-section';
import ChatbotDisclaimer from '../../components/chatbot-disclaimer/chatbot-disclaimer';

export interface SiteFooterApplicationLinks {
  url: string;
  image: string;
  alt: string;
  detail: string;
}

export interface SiteFooterSocialLink {
  name: string;
  logo: string;
  url: string;
  label: string;
}

export interface SiteFooterSocialLinks {
  node: SiteFooterSocialLink;
}

export interface SiteFooterOptionalProps {
  /** The children to be rendered. */
  children?: ReactNode;
  /** Adds the Apple & Google play store links to the footer. */
  applicationLinks?: SiteFooterApplicationLinks[];
  /** Append additional links to the footer bottom section. */
  bottomLinks?: Lucid.Directus.Generated.Links[];
  /** Allows for a customPrefix to overwrite gatsby withPrefix function. */
  customPrefix?: string;
  /** The Language Switcher Disabled Languages. */
  disabledLanguages?: Lucid.I18n.LanguagesShortCode[];
  /** The entity values to allow for customization of urls. */
  nodeTranslations?: Lucid.Directus.Custom.NodeTranslation[];
  /** Checks if the navbar is a visio page. */
  isVisio?: boolean;
  /** Will add the lucidchart.com/pages domain to chart-blog links going to that site if set to true */
  prependLinks?: boolean;
  /** Checks if the navbar should show security. */
  showSecurity?: boolean;
}

// TODO fix the menu type
export interface SiteFooterProps extends SiteFooterOptionalProps {
  /** The i18n translation data. */
  i18nData: Lucid.I18n.DataEdges;
  /** The menu list for the Footer component. */
  menu: Lucid.Directus.Generated.Footer_Column_Translations[];
}

const StyledFooter = styled.footer<Pick<SiteFooterOptionalProps, 'children'>>(({ theme }) => ({
  paddingTop: theme.space.s16,
  background: theme.colors.coolGrey[0],
}));

const Footer: FC<PropsWithChildren<SiteFooterProps>> = ({
  applicationLinks = [],
  bottomLinks = [],
  customPrefix,
  disabledLanguages = ['da'],
  nodeTranslations,
  i18nData,
  prependLinks = false,
  isVisio = false,
  menu,
}) => {
  const { shortCode, product } = useContext(LayoutContext);
  const currentLangName = formatLanguageKey(shortCode, 'name');
  const i18n = getI18nData(i18nData, shortCode);
  const visioQueryParams = '?type=visio';
  const baseLinks: Lucid.Directus.Generated.Links[] = [
    {
      id: '100',
      final_url: 'https://lucid.co/privacy',
      default_link_text: i18n('mktg-footer-about-privacy'),
    },
    {
      id: '101',
      final_url: 'https://lucid.co/legal',
      default_link_text: i18n('mktg-footer-legal'),
    },
    {
      id: '102',
      final_url: '#cookie-preference',
      default_link_text: i18n('mktg-footer-cookie-privacy-choices'),
    },
    {
      id: '103',
      final_url: 'https://lucid.co/cookies',
      default_link_text: i18n('mktg-footer-cookie-policy'),
    },
  ];

  const footerBottomLinks: Lucid.Directus.Generated.Links[] = [...baseLinks, ...bottomLinks];

  return (
    <StyledFooter>
      <Container css={{ display: 'flex' }}>
        <Box
          css={theme => ({
            display: 'grid',
            flexWrap: 'wrap',
            flex: '0 1 100%',
            gridTemplateColumns: `repeat(${menu.length <= 5 ? menu.length : 5}, minmax(min-content, 1fr))`,
            padding: `${theme.space.s24}px 0`,
            gap: theme.space.s8,
            [theme.breakpoints.bp1024.max]: {
              gridTemplateColumns: 'repeat(3, 1fr)',
            },
            [theme.breakpoints.bp500.max]: {
              gridTemplateColumns: 'repeat(2, 1fr)',
            },
          })}
        >
          {menu.map((column, index) => (
            <Box key={`footerMenuItem-${index + 1}`}>
              <Text aria-level={2} id={`footerLinksGroupLabel-${index + 1}`} role="heading" variant="headingS">
                {column.heading}
              </Text>
              <ul
                aria-labelledby={`footerLinksGroupLabel-${index + 1}`}
                css={theme => ({
                  flex: '0 1 20%',
                  listStyle: 'none',
                  margin: '0 0 2rem 0',
                  paddingLeft: 0,
                  [theme.breakpoints.bp900.max]: {
                    flex: '0 1 31%',
                    marginBottom: '2.5rem',
                  },
                  [theme.breakpoints.bp500.max]: {
                    flex: '0 1 49%',
                  },
                })}
              >
                {column?.link_set?.map(({ menu_link_id: link }, subIndex) => {
                  if (!link) return null;

                  if (prependLinks) {
                    link = prependInternalLink(link, product || '');
                  }
                  const url = link.final_url;
                  const href = isVisio && url?.includes('pricing') ? `${url}${visioQueryParams}` : url;

                  // TODO: figure out what this is supposed to do
                  // if (productUrl && link.fieldProductLink) {
                  //   href = `${productUrl}${shortLangPrefix !== 'en' ? `/${shortLangPrefix}` : ''}${href}`;
                  // }

                  const beaconEvent = link.beacon_id
                    ? ({
                        name: 'clickedMarketingPageElement',
                        elementLocation: 'footer',
                        elementDetail: link?.beacon_id,
                        elementType: 'link',
                      } as Lucid.Beacon.Event)
                    : null;

                  return (
                    <li
                      key={`footerLiItemLink-${index + subIndex + 1}`}
                      css={theme => ({
                        marginRight: 0,
                        paddingRight: theme.space.s24,
                        paddingBottom: theme.space.s8,
                        [theme.breakpoints.bp1024.max]: {
                          paddingBottom: theme.space.s24,
                        },
                      })}
                    >
                      <Link
                        beaconEvent={beaconEvent}
                        noPrefix={!!customPrefix}
                        // onClick={() => link.fieldBeaconElementDetail}
                        to={href}
                        variant="navLink"
                      >
                        {link.default_link_text}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </Box>
          ))}
        </Box>
      </Container>
      <Box
        css={theme => ({
          background: theme.colors.coolGrey[5],
          paddingTop: theme.space.s24,
          paddingBottom: theme.space.s24,
        })}
      >
        <Container
          css={theme => ({
            [theme.breakpoints.bp1440.min]: {
              paddingLeft: '2.75rem',
              paddingRight: '2.75rem',
            },
          })}
        >
          <Box
            css={theme => ({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              flexWrap: 'wrap',
              gap: theme.space.s24,
              justifyContent: 'space-between',
              marginBottom: theme.space.s8,
              [theme.breakpoints.bp1024.min]: {
                flexDirection: 'row',
                alignItems: 'flex-start',
              },
            })}
          >
            <Box
              css={theme => ({
                display: 'flex',
                columnGap: theme.space.s16,
                rowGap: theme.space.s32,
                flexWrap: 'wrap',
                justifyContent: 'flex-start',
                flex: '1 0 auto',
              })}
            >
              <LanguageSwitcher
                buttonAriaLabel={i18n('mktg-footer-language-switch', {
                  language: currentLangName,
                })}
                disabledLanguages={disabledLanguages}
                languageKey={shortCode}
                navAriaLabel={i18n('mktg-footer-nav-lang')}
                nodeTranslations={nodeTranslations!}
              />
              <Box
                css={theme => ({
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'stretch',
                  gap: `${theme.space.s8}px ${theme.space.s32}px`,
                })}
              >
                {footerBottomLinks.map((link, index) => {
                  const isCookiePreference = link.final_url?.includes('cookie-preference');

                  return (
                    <Link
                      key={`footerBottomLink-${index + 1}`}
                      css={{
                        '&, &:hover, &:focus': {
                          width: 'auto',
                          marginLeft: 0,
                          paddingLeft: 0,
                        },
                      }}
                      newTab={!isCookiePreference}
                      onClick={() => {
                        if (isBrowser() && isCookiePreference) {
                          window.Osano.cm.showDrawer();
                        }
                      }}
                      rel="noreferrer"
                      to={link.final_url}
                      underline="hover"
                      variant="navLink"
                    >
                      {link.default_link_text}
                    </Link>
                  );
                })}
              </Box>
            </Box>
            <SocialIconSection product={product} />
            {applicationLinks.length > 0 && (
              <Box
                css={theme => ({
                  display: 'flex',
                  flexWrap: 'wrap',
                  rowGap: theme.space.s32,
                  columnGap: theme.space.s8,
                  [theme.breakpoints.bp1024.min]: {
                    flexGrow: 1,
                    justifyContent: 'flex-end',
                  },
                })}
              >
                {applicationLinks.map((link, index) => (
                  <Link
                    key={`footerApplicationLinks-${index + 1}`}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onClick={() =>
                      sendBeaconEvent({
                        name: 'clickedMarketingPageElement',
                        elementLocation: 'footer',
                        elementDetail: link.detail,
                        elementType: 'link',
                      })
                    }
                    to={link.url}
                  >
                    <Image
                      css={{ height: '40px', width: '125px' }}
                      dataElementPageLocation="bottomFooter"
                      imageAlt={link.alt || ''}
                      imageSrc={link.image}
                      loading="lazy"
                    />
                  </Link>
                ))}
              </Box>
            )}
          </Box>
          <Text
            css={{
              marginBottom: 0,
              textAlign: 'right',
            }}
            variant="interactiveXS400"
          >
            <span aria-label="Copyright" role="img">
              &copy;
            </span>
            {` ${new Date().getFullYear()} Lucid Software Inc.`}
          </Text>
        </Container>
        <ChatbotDisclaimer i18nData={i18nData} />
      </Box>
    </StyledFooter>
  );
};

export { Footer };
export default Footer;
